/**
 * Auth admin API client - thin typed wrappers for the P3 surface:
 *   - /v1/auth/sso/idp-connections      (SAML + OIDC IdP CRUD)
 *   - /v1/auth/ldap/settings            (LDAP single-row settings)
 *   - /v1/auth/scim/tokens              (SCIM bearer-token issuance)
 *
 * These are admin endpoints - caller must be authenticated and have
 * the foundational:integration permission.
 */

import { apiFetch } from './api';

// ── SSO ──

export type IdpProtocol = 'saml' | 'oidc';

export interface IdpConnection {
  id: string;
  tenantId: string;
  protocol: IdpProtocol;
  displayName: string;
  enabled: boolean;
  defaultForTenant: boolean;
  issuerUrl: string | null;
  clockSkewMs: number;
  jitProvisioning: boolean;
  groupRoleMapping: Record<string, string>;
  deactivatedUserReject: boolean;
  // SAML
  samlIdpEntityId: string | null;
  samlIdpSsoUrl: string | null;
  samlIdpSloUrl: string | null;
  samlIdpCertPem: string | null;
  samlNameIdFormat: string | null;
  samlWantAssertionsSigned: boolean;
  samlWantResponseSigned: boolean;
  samlSignAuthnRequest: boolean;
  // OIDC
  oidcDiscoveryUrl: string | null;
  oidcClientId: string | null;
  oidcScopes: string;
  oidcRedirectUri: string | null;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  lastFailureMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSamlConnectionInput {
  protocol: 'saml';
  displayName: string;
  enabled?: boolean;
  defaultForTenant?: boolean;
  issuerUrl?: string;
  clockSkewMs?: number;
  jitProvisioning?: boolean;
  groupRoleMapping?: Record<string, string>;
  deactivatedUserReject?: boolean;
  idpEntityId: string;
  idpSsoUrl: string;
  idpSloUrl?: string;
  idpCertPem: string;
  idpMetadataXml?: string;
  nameIdFormat?: string;
  wantAssertionsSigned?: boolean;
  wantResponseSigned?: boolean;
  signAuthnRequest?: boolean;
}

export interface CreateOidcConnectionInput {
  protocol: 'oidc';
  displayName: string;
  enabled?: boolean;
  defaultForTenant?: boolean;
  issuerUrl?: string;
  clockSkewMs?: number;
  jitProvisioning?: boolean;
  groupRoleMapping?: Record<string, string>;
  deactivatedUserReject?: boolean;
  discoveryUrl: string;
  clientId: string;
  clientSecret: string;
  scopes?: string;
  redirectUri: string;
}

export async function listIdpConnections(): Promise<IdpConnection[]> {
  const r = await apiFetch<{ connections: IdpConnection[] }>('/v1/auth/sso/idp-connections');
  return r.connections;
}

export async function createIdpConnection(
  input: CreateSamlConnectionInput | CreateOidcConnectionInput,
): Promise<IdpConnection> {
  return apiFetch<IdpConnection>('/v1/auth/sso/idp-connections', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function deleteIdpConnection(id: string): Promise<void> {
  await apiFetch<void>(`/v1/auth/sso/idp-connections/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

// ── LDAP settings ──

export interface LdapSettings {
  id: string;
  tenantId: string;
  enabled: boolean;
  host: string;
  port: number;
  useLdaps: boolean;
  tlsMinVersion: string;
  caCertificatePem: string | null;
  rejectUnauthorized: boolean;
  bindDn: string;
  // bindPasswordKmsRef is NEVER returned to the client.
  userSearchBase: string;
  userSearchFilter: string;
  userAttributes: string;
  groupSearchBase: string | null;
  groupSearchFilter: string | null;
  groupRoleMapping: Record<string, string>;
  pageSize: number;
  requireSaslSigning: boolean;
  requireChannelBinding: boolean;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  lastFailureMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertLdapSettingsInput {
  enabled?: boolean;
  host: string;
  port?: number;
  useLdaps?: boolean;
  tlsMinVersion?: string;
  caCertificatePem?: string;
  rejectUnauthorized?: boolean;
  bindDn: string;
  /** Plaintext bind password. Encrypted server-side before storage. */
  bindPassword?: string;
  userSearchBase: string;
  userSearchFilter?: string;
  userAttributes?: string;
  groupSearchBase?: string;
  groupSearchFilter?: string;
  groupRoleMapping?: Record<string, string>;
  pageSize?: number;
  requireSaslSigning?: boolean;
  requireChannelBinding?: boolean;
}

export async function getLdapSettings(): Promise<LdapSettings | null> {
  try {
    return await apiFetch<LdapSettings>('/v1/auth/ldap/settings');
  } catch (err) {
    if ((err as { status?: number }).status === 404) return null;
    throw err;
  }
}

export async function upsertLdapSettings(input: UpsertLdapSettingsInput): Promise<LdapSettings> {
  return apiFetch<LdapSettings>('/v1/auth/ldap/settings', {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export async function deleteLdapSettings(): Promise<void> {
  await apiFetch<void>('/v1/auth/ldap/settings', { method: 'DELETE' });
}

// ── SCIM tokens ──

export interface ScimToken {
  id: string;
  name: string;
  displayPrefix: string;
  revokedAt: string | null;
  lastUsedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  createdBy: string | null;
}

export interface IssuedScimToken extends ScimToken {
  /** Shown ONCE on creation. Never recoverable after this response. */
  plaintext: string;
}

export interface CreateScimTokenInput {
  name: string;
  /** Optional. ISO-8601 date string. Null/omitted = never expires. */
  expiresAt?: string;
}

export async function listScimTokens(): Promise<ScimToken[]> {
  const r = await apiFetch<{ tokens: ScimToken[] }>('/v1/auth/scim/tokens');
  return r.tokens;
}

export async function issueScimToken(input: CreateScimTokenInput): Promise<IssuedScimToken> {
  return apiFetch<IssuedScimToken>('/v1/auth/scim/tokens', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function revokeScimToken(id: string): Promise<void> {
  await apiFetch<void>(`/v1/auth/scim/tokens/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
